import { LayoutGrid, List } from "lucide-react";

import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";
import usePrefTickets from "../../hooks/usePrefTickets";

// A escolha do motivo, nos dois modais de encerramento (um atendimento e o
// lote). Um componente só porque é a mesma pergunta: duas grades iguais
// mantidas em paralelo divergem na primeira alteração.
//
// Cartão mostra a frase de ajuda e é ótimo com quatro motivos; com vinte, vira
// uma parede que empurra o resto do modal para fora da tela. Por isso a lista:
// a escolha é de quem usa, fica gravada no perfil e vale nos dois modais.

// Acima disto o cartão deixa de caber: é a conta de duas colunas dentro de um
// modal de 448px de altura útil.
const MUITOS = 6;

const EscolherMotivoDeEncerramento = ({
	motivos,
	valor,
	onChange,
	rotulo = "Por que está encerrando?",
}) => {
	const [modo, setModo] = usePrefTickets("motivosEncerramentoComo", "auto");

	// "auto" é o padrão: com poucos motivos, cartões; com muitos, lista. Quem
	// escolher manda, e a escolha sobrevive ao F5.
	const emLista =
		modo === "lista" || (modo === "auto" && motivos.length > MUITOS);

	const escolhido = motivos.find((m) => m.chave === valor);

	return (
		<div className="space-y-1.5">
			<div className="flex items-center justify-between gap-2">
				<Label htmlFor={emLista ? "motivoDeEncerramento" : undefined}>
					{rotulo}
				</Label>
				{motivos.length > 1 && (
					<button
						type="button"
						onClick={() => setModo(emLista ? "cards" : "lista")}
						title={emLista ? "Ver em cartões" : "Ver em lista"}
						aria-label={
							emLista
								? "Ver os motivos em cartões"
								: "Ver os motivos em lista"
						}
						className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
					>
						{emLista ? (
							<LayoutGrid className="h-3.5 w-3.5" />
						) : (
							<List className="h-3.5 w-3.5" />
						)}
					</button>
				)}
			</div>

			{motivos.length === 0 && (
				<p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
					Nenhum motivo cadastrado. O atendimento encerra assim mesmo — o
					administrador cadastra os motivos em Configurações › Encerrar e reabrir.
				</p>
			)}

			{motivos.length > 0 &&
				(emLista ? (
					<>
						<Select value={valor || undefined} onValueChange={onChange}>
							<SelectTrigger id="motivoDeEncerramento">
								<SelectValue placeholder="Escolha o motivo" />
							</SelectTrigger>
							<SelectContent>
								{motivos.map((m) => (
									<SelectItem key={m.chave} value={m.chave}>
										{m.rotulo}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{/* A frase de ajuda é o que separa uma lista de rótulos de uma
						    escolha que a equipe faz igual: na lista ela não cabe na
						    opção, então acompanha a escolhida aqui embaixo. */}
						{escolhido?.ajuda && (
							<p className="text-xs leading-snug text-muted-foreground">
								{escolhido.ajuda}
							</p>
						)}
					</>
				) : (
					<div className="grid grid-cols-2 gap-2">
						{motivos.map((m) => (
							<button
								key={m.chave}
								type="button"
								onClick={() => onChange(m.chave)}
								className={cn(
									"rounded-lg border p-2.5 text-left transition-colors",
									valor === m.chave
										? "border-primary bg-primary/5"
										: "hover:bg-muted",
								)}
							>
								<span className="block text-sm font-medium">{m.rotulo}</span>
								<span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
									{m.ajuda}
								</span>
							</button>
						))}
					</div>
				))}
		</div>
	);
};

export default EscolherMotivoDeEncerramento;
